import api from "../../api/client";
import { errorToast, successToast } from '../../helper/FormHelper'
import { t } from '../../i18n/t';
import { reportApiError } from '../../api/errors'
import { BaseURL, ShopList, Shop, AssignableShops, Me } from "../helper/Config";
import { reconcileShops, getShops, getActiveShopId, setActiveShopId } from '../../shop/activeShop'
import { setUserDetails } from '../helper/SessionHelper'

/**
 * Shops (BE-02, BE-03, BE-16).
 *
 * Deliberately NOT backed by a redux slice, unlike every other resource here.
 * The active shop has to be readable from the axios request interceptor, which
 * runs outside React and therefore outside the store — so activeShop.js owns
 * it, and this module is the thin layer that fills activeShop.js from the API.
 * One source of truth beats a store and a localStorage copy drifting apart.
 */

/**
 * Fetch the shops this user may work in and reconcile the active selection.
 *
 * Returns the shop list, or null if the call failed.
 *
 * A failure here is deliberately quiet — no toast. This runs on layout mount,
 * so a toast would fire on every page load whenever the endpoint is briefly
 * unreachable, and there is nothing the user can do about it. What matters is
 * that reconcileShops is NOT called on failure: a transient network error must
 * not clear a working shop selection and leave the till 422-ing on every save.
 */
export async function GetShopList() {
    try {
        const result = await api.get(BaseURL + ShopList)

        if (result.data.status === true) {
            // A bare array in `data`, not a paginator — /shops is an
            // entitlement list, and nobody is entitled to 50 shops.
            const shops = Array.isArray(result.data.data) ? result.data.data : []
            reconcileShops(shops)
            return shops
        }

        return null
    } catch (e) {
        return null
    }
}

/**
 * One shop, including the warehouses linked to it.
 *
 * The warehouse list is the reason this exists separately from GET /shops:
 * warehouses are linked to shops rather than owned by one (BE-02a), so "which
 * godowns can this sale ship from" is a per-shop question that the entitlement
 * list does not answer.
 */
export async function GetShopById(id) {
    try {
        const result = await api.get(BaseURL + Shop + "/" + id)

        if (result.data.status === true) return result.data.data

        errorToast(result.data.message)
        return null
    } catch (e) {
        reportApiError(e)
        return null
    }
}

/**
 * Update the shop's own details — the invoice header (BE-16).
 *
 * Requires `shop.update`, which most staff will not hold; a 403 here is a
 * normal outcome and reportApiError now says "you do not have permission to do
 * this" rather than "something went wrong", because BE-04 made the status code
 * truthful enough to tell the difference.
 *
 * On success the entitlement cache is refreshed rather than patched in place.
 * The invoice reads its letterhead from that cache, and a shop whose name was
 * just corrected must not keep printing the old one until the next login.
 */
export async function ShopUpdateApiService(id, postBody) {
    try {
        const result = await api.put(BaseURL + Shop + "/" + id, postBody)

        if (result.data.status === true) {
            successToast(t('api.requestSuccessful'))
            await GetShopList()
            return true
        }

        errorToast(result.data.message)
        return false
    } catch (e) {
        reportApiError(e)
        return false
    }
}

/**
 * The entitlement list, fetching it only if we have never had one.
 *
 * For callers that need the list to render (the picker, the invoice) but must
 * not stall behind a request on every mount.
 */
export async function EnsureShopList() {
    const cached = getShops()
    if (cached.length) return cached
    return (await GetShopList()) || []
}

/**
 * Every ACTIVE shop, for assigning users to (B-7).
 *
 * NOT the same list as GetShopList, and the difference is the single easiest
 * thing to get wrong in this feature. `/shops` is the caller's OWN entitlement
 * list — an owner assigning a manager to a branch they do not personally work
 * in would simply not find it there. `/shops` also feeds reconcileShops, so
 * widening it would make the client believe it is entitled to shops it is not.
 *
 * Requires `user.store`: the only reason to see every shop is to put somebody
 * in one.
 */
export async function GetAssignableShopList() {
    try {
        const result = await api.get(BaseURL + AssignableShops)

        if (result.data.status === true) {
            return Array.isArray(result.data.data) ? result.data.data : []
        }

        return []
    } catch (e) {
        reportApiError(e)
        return []
    }
}

/**
 * Change which shop the user is working in.
 *
 * WHY THIS IS NOT JUST setActiveShopId + reload
 *
 * A reload was enough while the shop decided only WHICH ROWS you saw. It is not
 * any more: the shop now also decides WHAT YOU MAY DO. A reload rehydrates
 * permissions from localStorage — the PREVIOUS shop's set — so a Manager in
 * Uttara who switches to Dhaka Main, where they are only a Cashier, would carry
 * the Manager sidebar with them until they logged out.
 *
 * So the new permissions are fetched and stored FIRST, and only then does the
 * caller reload. GET /me is the switch endpoint: it returns the permissions for
 * whichever shop the request names (B-12), which is why there is no
 * POST /shop/switch to call instead.
 *
 * @returns {Promise<boolean>} false if the switch failed. The caller must NOT
 *          reload in that case — the previous selection has been restored here,
 *          and reloading with a new shop id and stale permissions is the exact
 *          silently-wrong state this function exists to prevent.
 */
export async function SwitchShop(shopId) {
    const previous = getActiveShopId()

    setActiveShopId(shopId)

    try {
        const result = await api.get(BaseURL + Me, {
            // Set explicitly rather than left to the request interceptor. The
            // interceptor reads localStorage, which was written one line ago,
            // and a header this consequential should not rest on that ordering.
            headers: shopId ? { 'X-Shop-Id': String(shopId) } : {},
        })

        if (result.data.status !== true) {
            setActiveShopId(previous)
            return false
        }

        const payload = result.data.data || {}

        setUserDetails(payload.user || {})

        if (Array.isArray(payload.shops)) {
            reconcileShops(payload.shops)
            // reconcileShops clears the selection when there is more than one
            // shop and none is stored yet. Ours is a deliberate choice, so it
            // is re-asserted afterwards.
            setActiveShopId(shopId)
        }

        return true
    } catch (e) {
        setActiveShopId(previous)
        reportApiError(e)
        return false
    }
}
